import type { InfiniteData, QueryClient } from '@tanstack/react-query'

import type { ChatHistoryMessage } from './chat-history'
import type { GroupDetail, GroupOrder } from './schema'

type CachedGroupDetail = GroupDetail | InfiniteData<GroupDetail>

function ordersFromCachedDetail(detail: CachedGroupDetail): GroupOrder[] {
  if ('pages' in detail) {
    return detail.pages.flatMap((page) => page.orders ?? [])
  }

  return detail.orders ?? []
}

/** Resolve chat bubbles for one line item, matching API index or list position. */
export function pickOrderItemChatHistory(
  order: GroupOrder | undefined,
  itemIndex: number,
  itemArrayIndex?: number,
): ChatHistoryMessage[] {
  if (!order) return []

  const items = order.items ?? []
  const byApiIndex = items.find((entry) => entry.index === itemIndex)
  if (byApiIndex?.chatHistory.length) return byApiIndex.chatHistory

  if (itemArrayIndex != null && itemArrayIndex >= 0) {
    const byPosition = items[itemArrayIndex]
    if (byPosition?.chatHistory.length) return byPosition.chatHistory
  }

  return byApiIndex?.chatHistory ?? []
}

/** Read chat from any cached group-detail query (current tab filter). */
export function readOrderItemChatFromCache(
  queryClient: QueryClient,
  groupId: string,
  orderId: string,
  itemIndex: number,
  itemArrayIndex?: number,
): ChatHistoryMessage[] {
  const cached = queryClient.getQueriesData<CachedGroupDetail>({
    queryKey: ['group', 'detail', groupId],
  })

  for (const [, detail] of cached) {
    if (!detail) continue

    const order = ordersFromCachedDetail(detail).find((entry) => entry.id === orderId)
    const messages = pickOrderItemChatHistory(order, itemIndex, itemArrayIndex)
    if (messages.length > 0) return messages
  }

  return []
}
